import { ColorOptions, IWPImage, IWPLink, IWPTitleParts } from "@/src/types";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import {
  isLight,
  nlToBr,
  gatsbyImageHelper,
  sendMessageToQteRest,
} from "@/src/helpers";
import S from "./ContactFormProduct.styled";
import Checkbox from "@/styles/components/Checkbox";
import axios from "axios";
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider,
} from "react-google-recaptcha-v3";
import { RadioButton } from "@/styles/components";
import TitleParts from "@/components/global-components/TitleParts";
export interface IContactFormProps {
  blockId: string;
  formPartiallyHidden: "yes" | "no";
  backgroundType: "card" | "fill";
  backgroundColor: ColorOptions;
  illustration: IWPImage;
  titleType: "h1" | "h2";
  titleParts: IWPTitleParts;
  title: string;
  text: string;
  privacyPolicyLink: IWPLink;
  submitButtonText: string;
  verticals: "SMART_METERING" | "SMART_BUILDING" | "SMART_TRACKING" | "OTHER"[];
}

type RequirementsType = {
  categories: {
    id: string;
    name: string;
  }[];
  deviceTypes: {
    id: string;
    name: string;
  }[];
  sensorTypes: {
    id: string;
    name: string;
  }[];
};

const EmptyRequirements: RequirementsType = {
  categories: [],
  deviceTypes: [],
  sensorTypes: [],
};

type FormData = {
  validTo: string;
  email: string;
  content: string;
  requirementCategoryIds: string[];
  requirementDeviceTypeIds: string[];
  requirementSensorTypeIds: string[];
  recaptchaToken: string;
  legalConsent: boolean;
  scopeSize: number;
  verticals: string[];
  maxDistanceBetweenDevices: number;
  companyName: string;
  productionDate: string;
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

const emptyForm = (): FormData => ({
  validTo: "",
  email: "",
  content: "",
  requirementCategoryIds: [],
  requirementDeviceTypeIds: [],
  requirementSensorTypeIds: [],
  verticals: [],
  recaptchaToken: "",
  legalConsent: false,
  scopeSize: 1,
  maxDistanceBetweenDevices: 0,
  companyName: "",
  productionDate: "",
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyForm(),
  success: false,
});

const ContactFormProducts: React.FC<IContactFormProps> = ({
  blockId = "",
  formPartiallyHidden,
  backgroundType,
  backgroundColor,
  illustration,
  titleType,
  titleParts,
  title,
  text,
  privacyPolicyLink,
  submitButtonText,
}) => {
  const textColor =
    backgroundColor === "yellow"
      ? "brown"
      : isLight(backgroundColor)
        ? "black"
        : "creme";
  const linkColor = (color: "white" | "red") =>
    color === "red" && backgroundColor !== "red" ? "red" : "white";

  const [formState, setFormState] = useState(emptyFormState());
  const [data, setData] = useState<RequirementsType>(EmptyRequirements);
  const [hideForm, setHideForm] = useState(formPartiallyHidden === "yes");
  const [isOpen, setIsOpen] = useState("");
  React.useEffect(() => {
    if (isOpen === "") return;

    const offset = 100; // Offset to apply some margin from the top

    const scrollToElement = (elementId: string) => {
      const element = document.getElementById(elementId);
      setTimeout(() => {
        if (element) {
          const topPosition =
            element.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({
            top: topPosition,
            behavior: "smooth",
          });
        }
      }, 350);
    };
    switch (isOpen) {
      case "category":
        scrollToElement("category");
        break;
      case "deviceType":
        scrollToElement("deviceType");
        break;
      case "sensorType":
        scrollToElement("sensorType");
        break;
      case "vertical":
        scrollToElement("vertical");
        break;
      default:
        break;
    }
  }, [isOpen]);

  const handleRecaptcha = useCallback((token: any) => {
    updateFormProperty("recaptchaToken", token);
  }, []);

  const getData = async () => {
    try {
      const response = await fetch(
        process.env.GATSBY_API_URL + "/requirements" || ""
      );
      const data = await response.json();
      setData(data);
    } catch (error: unknown) {
      console.error("Error fetching data", error);
    }
  };

  //on success, scroll to thank you div and show success message
  React.useEffect(() => {
    if (formState.success) {
      const thankYouDiv = document.getElementById("thank-you-div");
      const offset = 100;
      if (thankYouDiv) {
        const topPosition =
          thankYouDiv.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({
          top: topPosition,
          behavior: "smooth",
        });
      }
    }
  }, [formState.success]);

  React.useEffect(() => {
    getData();
  }, []);

  function updateFormProperty<K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ) {
    setFormState((state) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({});

  const onSubmit = handleSubmit(() => {
    if (formState.data.verticals.length === 0) {
      // Trigger an error if no vertical is selected
      setFormState((prev) => ({
        ...prev,
        error: "At least one vertical must be selected",
      }));
      return;
    }

    setFormState((state) => ({ ...state, isLoading: true }));

    //add 24h to date
    const newValidTo = new Date(formState.data.validTo);
    newValidTo.setDate(newValidTo.getDate() + 1);

    const newProductionDate = new Date(formState.data.productionDate);
    newProductionDate.setDate(newProductionDate.getDate() + 1);

    axios({
      method: "post",
      url: process.env.GATSBY_API_URL + "/rfis" || "",
      data: {
        validTo: newValidTo,
        email: formState.data.email,
        content: formState.data.content,
        scopeSize: 100000,
        requirementCategoryIds: formState.data.requirementCategoryIds,
        requirementDeviceTypeIds: formState.data.requirementDeviceTypeIds,
        requirementSensorTypeIds: formState.data.requirementSensorTypeIds,
        maxDistanceBetweenDevices: formState.data.maxDistanceBetweenDevices,
        companyName: formState.data.companyName,
        productionDate: newProductionDate,
        verticals: formState.data.verticals,
        recaptchaToken: formState.data.recaptchaToken,
      },
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (response.status === 201) {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            success: true,
          }));
        }
      })
      .catch((error) => {
        setFormState((state) => ({
          ...state,
          isLoading: false,
          error: error.message,
        }));
        console.error("Error sending message", error);
        sendMessageToQteRest(error.message, "rfi");
      });
  });

  // Add a helper for rendering the error message
  const renderVerticalsError = () => {
    if (formState.error) {
      //open accordion, details/summary
      const offeringDetails = document.getElementById("vertical");
      if (offeringDetails && offeringDetails instanceof HTMLDetailsElement) {
        offeringDetails.open = true;

        const offset = 100;
        const topPosition =
          offeringDetails.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({
          top: topPosition,
          behavior: "smooth",
        });
      }
      return <S.ErrorMsg>{formState.error}</S.ErrorMsg>;
    }
    return null;
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    keyName: keyof FormData
  ) => {
    updateFormProperty(keyName, event.target.checked);
  };

  const imageData = gatsbyImageHelper(illustration);

  const renderCategoriesAccordion = () => {
    const renderCategories = data.categories.map((category) => (
      <Checkbox
        key={category.id}
        checked={formState.data.requirementCategoryIds.includes(category.id)}
        onChange={(e) => {
          const isChecked = e.target.checked;
          const newIds = isChecked
            ? [...formState.data.requirementCategoryIds, category.id]
            : formState.data.requirementCategoryIds.filter(
                (id) => id !== category.id
              );
          updateFormProperty("requirementCategoryIds", newIds);
        }}
        colorOptions={backgroundColor}
        label={category.name}
      />
    ));

    return (
      <S.Accordion id="category">
        <S.AccordionTitle
          onClick={() => setIsOpen(isOpen === "category" ? "" : "category")}
        >
          <span>Product/Solution needs</span>
          <S.AccordionArrow className="accordion-arrow category-arrow" />
        </S.AccordionTitle>
        <S.Checkboxes>{renderCategories}</S.Checkboxes>
      </S.Accordion>
    );
  };

  const renderDeviceTypesAccordion = () => {
    const renderDeviceTypes = data.deviceTypes.map((deviceType) => (
      <Checkbox
        key={deviceType.id}
        checked={formState.data.requirementDeviceTypeIds.includes(
          deviceType.id
        )}
        onChange={(e) => {
          const isChecked = e.target.checked;
          const newIds = isChecked
            ? [...formState.data.requirementDeviceTypeIds, deviceType.id]
            : formState.data.requirementDeviceTypeIds.filter(
                (id) => id !== deviceType.id
              );
          updateFormProperty("requirementDeviceTypeIds", newIds);
        }}
        label={deviceType.name}
        colorOptions={backgroundColor}
      />
    ));

    return (
      <S.Accordion id="deviceType">
        <S.AccordionTitle
          onClick={() => setIsOpen(isOpen === "deviceType" ? "" : "deviceType")}
        >
          <span>Device type</span>
          <S.AccordionArrow className="accordion-arrow device-type-arrow" />
        </S.AccordionTitle>
        <S.Checkboxes>{renderDeviceTypes}</S.Checkboxes>
      </S.Accordion>
    );
  };

  const renderSensorTypesAccordion = () => {
    const renderSensorTypes = data.sensorTypes.map((sensorType) => (
      <Checkbox
        key={sensorType.id}
        checked={formState.data.requirementSensorTypeIds.includes(
          sensorType.id
        )}
        onChange={(e) => {
          const isChecked = e.target.checked;
          const newIds = isChecked
            ? [...formState.data.requirementSensorTypeIds, sensorType.id]
            : formState.data.requirementSensorTypeIds.filter(
                (id) => id !== sensorType.id
              );
          updateFormProperty("requirementSensorTypeIds", newIds);
        }}
        label={sensorType.name}
        colorOptions={backgroundColor}
      />
    ));

    return (
      <S.Accordion id="sensorType">
        <S.AccordionTitle
          onClick={() => setIsOpen(isOpen === "sensorType" ? "" : "sensorType")}
        >
          <span>Needed sensors</span>
          <S.AccordionArrow className="accordion-arrow sensor-type-arrow" />
        </S.AccordionTitle>
        <S.Checkboxes>{renderSensorTypes}</S.Checkboxes>
      </S.Accordion>
    );
  };

  const renderVerticals = () => {
    const verticals = [
      "SMART_METERING",
      "SMART_BUILDING",
      "SMART_TRACKING",
      "OTHER",
    ];
    //one answer is mandatory

    const renderVerticals = verticals.map((vertical) => {
      const label =
        vertical === "OTHER"
          ? "Other"
          : vertical === "SMART_METERING"
            ? "Smart metering"
            : vertical === "SMART_BUILDING"
              ? "Smart building"
              : vertical === "SMART_TRACKING"
                ? "Smart tracking"
                : vertical;
      return (
        <Checkbox
          key={vertical}
          checked={formState.data.verticals.includes(vertical)}
          label={label}
          onChange={(e) => {
            const isChecked = e.target.checked;
            const newIds = isChecked
              ? [...formState.data.verticals, vertical]
              : formState.data.verticals.filter((id) => id !== vertical);
            updateFormProperty("verticals", newIds);
          }}
          colorOptions={backgroundColor}
        />
      );
    });

    return (
      <>
        {renderVerticalsError()} {/* Error message if none selected */}
        <S.Accordion open id="vertical">
          <S.AccordionTitle
            onClick={() => setIsOpen(isOpen === "vertical" ? "" : "vertical")}
          >
            <span>Verticals *</span>
            <S.AccordionArrow className="accordion-arrow vertical-arrow" />
          </S.AccordionTitle>
          <S.Checkboxes>{renderVerticals}</S.Checkboxes>
        </S.Accordion>
      </>
    );
  };

  const today = new Date().toISOString().split("T")[0];
  //today + 7 days
  const validTo = new Date(today);
  validTo.setDate(validTo.getDate() + 7);
  const validToString = validTo.toISOString().split("T")[0];
  return (
    <S.Section
      $backgroundColor={backgroundColor}
      $backgroundType={backgroundType}
      id="contact-product-section"
    >
      <S.Inner id={blockId}>
        <S.InnerContainer
          $backgroundColor={backgroundColor}
          $backgroundType={backgroundType}
          $textColor={textColor}
        >
          <S.Container>
            {formState.success ? (
              <S.ImageTitleWrapper>
                <div id="thank-you-div">
                  {titleType === "h1" && (
                    <S.TitleH1>Thank you for your request!</S.TitleH1>
                  )}
                  {titleType === "h2" && (
                    <S.TitleH2>Thank you for your request!</S.TitleH2>
                  )}
                  <S.Text>
                    You will receive a confirmation email shortly.
                  </S.Text>
                </div>
                {imageData && (
                  <S.Image
                    image={imageData}
                    alt={illustration.altText}
                    objectFit="contain"
                  />
                )}
              </S.ImageTitleWrapper>
            ) : (
              <div>
                <S.ImageTitleWrapper>
                  {titleType === "h1" && (
                    <S.TitleH1>
                      {titleParts ? (
                        <TitleParts
                          titleParts={titleParts}
                          backgroundColor={backgroundColor}
                        />
                      ) : (
                        title && title
                      )}
                    </S.TitleH1>
                  )}
                  {titleType === "h2" && (
                    <S.TitleH2>
                      {titleParts ? (
                        <TitleParts
                          titleParts={titleParts}
                          backgroundColor={backgroundColor}
                        />
                      ) : (
                        title && title
                      )}
                    </S.TitleH2>
                  )}
                  {imageData && (
                    <S.Image
                      image={imageData}
                      alt={illustration.altText}
                      objectFit="cover"
                    />
                  )}
                </S.ImageTitleWrapper>
                {text && <S.Subtitle>{nlToBr(text)}</S.Subtitle>}

                {/* form starts here */}
                <GoogleReCaptchaProvider
                  reCaptchaKey="6LcOliYqAAAAAA_t0pgm0QA1p4OZrj9u8eRz_NmC"
                  container={{
                    element: "div",
                    parameters: {
                      badge: "bottomleft",
                      theme: "dark",
                    },
                  }}
                >
                  <GoogleReCaptcha onVerify={handleRecaptcha} />

                  <S.Form $hideForm={hideForm} onSubmit={onSubmit}>
                    <S.HiddenFormOverlay
                      onClick={() => setHideForm(false)}
                      $hideForm={hideForm}
                      $backgroundColor={backgroundColor}
                    />

                    {/* Valid to */}
                    <S.InputWrapper>
                      <S.Text>Valid through *</S.Text>
                      <S.Input
                        onFocus={() => setHideForm(false)}
                        type="date"
                        placeholder="Valid to *"
                        min={validToString}
                        {...register("validTo", { required: true })}
                        onChange={(e) => {
                          updateFormProperty("validTo", e.target.value);
                          setValue("validTo", e.target.value);
                        }}
                      />
                      {errors.validTo && (
                        <S.ErrorMsg>Valid to is required</S.ErrorMsg>
                      )}
                    </S.InputWrapper>

                    {/* Email */}
                    <S.InputWrapper>
                      <S.Input
                        onFocus={() => setHideForm(false)}
                        type="email"
                        id="email"
                        placeholder=" "
                        {...register("email", { required: true })}
                        onChange={(e) => {
                          updateFormProperty("email", e.target.value);
                          setValue("email", e.target.value);
                        }}
                      />
                      <S.Label htmlFor="email">Email *</S.Label>
                      {errors.email && (
                        <S.ErrorMsg>Email is required</S.ErrorMsg>
                      )}
                    </S.InputWrapper>

                    {/* Company name */}
                    <S.InputWrapper>
                      <S.Input
                        type="text"
                        id="companyName"
                        placeholder=" "
                        {...register("companyName", { required: false })}
                        onChange={(e) => {
                          updateFormProperty("companyName", e.target.value);
                        }}
                      />
                      <S.Label htmlFor="companyName">Company name</S.Label>
                    </S.InputWrapper>

                    {/* Max distance between devices */}
                    <S.InputWrapper>
                      <S.Input
                        type="number"
                        min={0}
                        id="maxDistanceBetweenDevices"
                        placeholder=" "
                        {...register("maxDistanceBetweenDevices", {
                          required: true,
                        })}
                        onChange={(e) => {
                          updateFormProperty(
                            "maxDistanceBetweenDevices",
                            parseInt(e.target.value)
                          );
                        }}
                      />
                      <S.Label htmlFor="maxDistanceBetweenDevices">
                        Max distance between devices (meters) *
                      </S.Label>
                      {errors.maxDistanceBetweenDevices && (
                        <S.ErrorMsg>
                          Max distance between devices is required
                        </S.ErrorMsg>
                      )}
                    </S.InputWrapper>

                    {/* Production date */}
                    <S.InputWrapper>
                      <S.Text>Production date </S.Text>
                      <S.Input
                        type="date"
                        min={today}
                        placeholder="Production date "
                        {...register("productionDate", { required: false })}
                        onChange={(e) => {
                          updateFormProperty("productionDate", e.target.value);
                        }}
                      />
                    </S.InputWrapper>

                    <div>
                      {/* Requirement Verticals */}
                      {renderVerticals()}

                      {/* Requirement Category */}
                      {renderCategoriesAccordion()}

                      {/* Requirement Device Type */}
                      {renderDeviceTypesAccordion()}

                      {/* Requirement Sensor Type */}
                      {renderSensorTypesAccordion()}
                    </div>

                    {/* Radio buttons */}
                    <S.RadioWrapper>
                      <S.RadioTitle>Project size:</S.RadioTitle>
                      <div onClick={() => updateFormProperty("scopeSize", 1)}>
                        <RadioButton
                          id="radio1"
                          checked={formState.data.scopeSize === 1}
                          label="< 1000 units"
                          textColor={textColor}
                        />
                      </div>
                      <div
                        onClick={() => updateFormProperty("scopeSize", 1000)}
                      >
                        <RadioButton
                          id="radio1"
                          checked={formState.data.scopeSize === 1000}
                          label="1000 - 5000 units"
                          textColor={textColor}
                        />
                      </div>
                      <div
                        onClick={() => updateFormProperty("scopeSize", 5000)}
                      >
                        <RadioButton
                          id="radio2"
                          checked={formState.data.scopeSize === 5000}
                          label="5000 - 10 000 units"
                          textColor={textColor}
                        />
                      </div>
                      <div
                        onClick={() => updateFormProperty("scopeSize", 10000)}
                      >
                        <RadioButton
                          id="radio3"
                          checked={formState.data.scopeSize === 10000}
                          label="10 000 - 100 000 units"
                          textColor={textColor}
                        />
                      </div>
                      <div
                        onClick={() => updateFormProperty("scopeSize", 100000)}
                      >
                        <RadioButton
                          id="radio4"
                          checked={formState.data.scopeSize === 100000}
                          label="100 000 - 500 000 units"
                          textColor={textColor}
                        />
                      </div>
                      <div
                        onClick={() => updateFormProperty("scopeSize", 500000)}
                      >
                        <RadioButton
                          id="radio5"
                          checked={formState.data.scopeSize === 500000}
                          label="Over 500 000 units"
                          textColor={textColor}
                        />
                      </div>
                    </S.RadioWrapper>

                    {/* Message */}
                    <S.InputWrapper>
                      <S.Textarea
                        placeholder=" "
                        id="content"
                        {...register("content", { required: false })}
                        onChange={(e) => {
                          updateFormProperty("content", e.target.value);
                          setValue("content", e.target.value);
                        }}
                      />
                      <S.Label htmlFor="content">
                        Describe your use case or needs
                      </S.Label>
                    </S.InputWrapper>

                    {/* privacy policy */}
                    <S.InputWrapper>
                      <Checkbox
                        checked={formState.data.legalConsent}
                        {...register("legalConsent", { required: true })}
                        onChange={(e) => {
                          handleCheckboxChange(e, "legalConsent");
                          setValue("legalConsent", e.target.checked);
                        }}
                        link={privacyPolicyLink}
                        colorOptions={backgroundColor}
                      />
                      {errors.legalConsent && (
                        <S.ErrorMsg>Legal consent is required</S.ErrorMsg>
                      )}
                    </S.InputWrapper>

                    <S.Text>{submitButtonText}</S.Text>
                    <S.Submit disabled={formState.isLoading} type="submit">
                      SEND
                    </S.Submit>
                  </S.Form>
                </GoogleReCaptchaProvider>
              </div>
            )}
          </S.Container>
        </S.InnerContainer>
      </S.Inner>
    </S.Section>
  );
};

export default ContactFormProducts;
